// Boundary layer service for administrative boundaries

import L from 'leaflet';
import { BOUNDARY_DATA, LAYER_STYLES } from '../constants';

// GeoJSON feature properties for regions and departments are not used directly
// in this service; the objects are passed to Leaflet and consumed by its
// type definitions when needed.

/**
 * Service for loading and managing administrative boundary layers
 */
export class BoundaryService {
  private regionLayer: L.GeoJSON | null = null;
  private departmentLayer: L.GeoJSON | null = null;
  private map: L.Map | null = null;

  /**
   * Set the map instance
   * @param map - Leaflet map instance
   */
  setMap(map: L.Map): void {
    this.map = map;
  }

  /**
   * Toggle region layer visibility
   * @param visible - Whether to show the layer
   */
  async toggleRegions(visible: boolean): Promise<void> {
    if (visible) {
      if (!this.regionLayer) {
        await this.loadRegionLayer();
      } else if (this.map && !this.map.hasLayer(this.regionLayer)) {
        this.regionLayer.addTo(this.map);
        // Ensure region layer is always above department layer when shown
        try {
          this.regionLayer.bringToFront();
        } catch {}
      }
    } else {
      if (this.regionLayer && this.map) {
        this.map.removeLayer(this.regionLayer);
      }
    }
  }

  /**
   * Toggle department layer visibility
   * @param visible - Whether to show the layer
   */
  async toggleDepartments(visible: boolean): Promise<void> {
    if (visible) {
      if (!this.departmentLayer) {
        await this.loadDepartmentLayer();
      } else if (this.map && !this.map.hasLayer(this.departmentLayer)) {
        this.departmentLayer.addTo(this.map);
        // If both layers present, keep region layer above departments
        try {
          this.regionLayer?.bringToFront();
        } catch {}
      }
    } else {
      if (this.departmentLayer && this.map) {
        this.map.removeLayer(this.departmentLayer);
      }
    }
  }

  /**
   * Load French regions from imported geojson data
   */
  private async loadRegionLayer(): Promise<void> {
    if (!this.map) return;

    try {
      // Create non-interactive (not-clickable) region layer and avoid popups
      this.regionLayer = L.geoJSON(BOUNDARY_DATA.regions, {
        interactive: false,
        style: () => LAYER_STYLES.region,
        // onEachFeature could be used for hover/click handlers; omit popups to keep layer non-clickable
        onEachFeature: (_feature, _layer) => {},
      });

      try {
        this.regionLayer.addTo(this.map);
        // If a department layer is also present, make sure regions are on top
        try {
          this.regionLayer.bringToFront();
        } catch {}
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('BoundaryService: failed to add region layer to map', err);
      }
    } catch (error) {
      console.error('Error loading region layer:', error);
      throw error;
    }
  }

  /**
   * Load French departments from imported geojson data
   */
  private async loadDepartmentLayer(): Promise<void> {
    if (!this.map) return;

    try {
      // Create non-interactive (not-clickable) department layer and avoid popups
      this.departmentLayer = L.geoJSON(BOUNDARY_DATA.departements, {
        interactive: false,
        style: () => LAYER_STYLES.department,
        // Do not bind popups so departments are not clickable
        onEachFeature: (_feature, _layer) => {},
      });

      try {
        this.departmentLayer.addTo(this.map);
        // Keep region layer visually above after adding departments
        try {
          this.regionLayer?.bringToFront();
        } catch {}
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('BoundaryService: failed to add department layer to map', err);
      }
    } catch (error) {
      console.error('Error loading department layer:', error);
      throw error;
    }
  }

  /**
   * Clean up layers
   */
  destroy(): void {
    if (this.regionLayer && this.map) {
      try {
        this.map.removeLayer(this.regionLayer);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('BoundaryService: failed to remove region layer', err);
      }
    }
    if (this.departmentLayer && this.map) {
      try {
        this.map.removeLayer(this.departmentLayer);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('BoundaryService: failed to remove department layer', err);
      }
    }
    this.regionLayer = null;
    this.departmentLayer = null;
  }
}
